import React from 'react';

import { useQueryMetadataList } from '@lumapps/metadata/hooks/useQueryMetadataList';
import { QueryMetadataListOptions } from '@lumapps/metadata/types';
import { WithRequiredProperty } from '@lumapps/utils/types/WithRequiredProperty';

import { DEFAULT_METADATA_PICKER_REQUEST_FIELDS } from '../constants';
import { MetadataTree, buildMetadataTree } from '../utils/buildMetadataTree';

export type UseFetchMetadataFamilyTreeProps = {
    /**
     * Metadata list params
     */
    params: WithRequiredProperty<QueryMetadataListOptions, 'familyId'>;
    /** Whether the hook should fetch */
    shouldFetch?: boolean;
    /** whether the tree built should show multiple levels or just the first one */
    showMultipleLevels?: boolean;
};

/**
 * Hook to fetch all metadata belonging to a family.
 *
 * ! This will make a recursive `list` call to get all pages until the list is complete.
 * ! To avoid too many calls, the default maxResults is set as 9999 with very minimal projection fields.
 * ! You can change these attributes using the given props but beware that a bigger projection
 * ! will cause a slower and heavier call.
 */
export const useFetchMetadataFamilyTree = ({
    params: { fields: extraFields = [], ...baseParams },
    shouldFetch,
    showMultipleLevels = false,
}: UseFetchMetadataFamilyTreeProps) => {
    const fields = [...DEFAULT_METADATA_PICKER_REQUEST_FIELDS, ...extraFields];
    const list = useQueryMetadataList(
        baseParams.source === 'instance'
            ? {
                  withInheritance: true,
                  ...baseParams,
                  fields,
              }
            : {
                  ...baseParams,
                  fields,
                  // If multiple levels are desired, we remove the parent property so that the backend
                  // only filters by familyId, therefore return the entire tree.
                  parent: showMultipleLevels ? undefined : baseParams.familyId,
              },
        shouldFetch,
    );

    const metadataList = React.useMemo((): MetadataTree => {
        if (!list.data?.length) {
            return [];
        }
        return buildMetadataTree(list.data, baseParams.familyId);
    }, [baseParams, list]);

    return {
        status: list.baseLoadingStatus,
        metadataList,
    };
};
